import { useNavigate } from 'react-router-dom';
import { User, Menu, LogOut, LogIn, UserPlus, Briefcase, Calendar, CalendarCheck, MapPin, Users, Bookmark, Clock, FolderHeart, Star, Lock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { SupportButton } from "@/components/support/SupportButton";
import { Button } from "@/components/ui/button";
import { useState } from 'react';

interface UserMenuProps {
  isHost: boolean;
  isAuthenticated: boolean;
  isLoggingOut?: boolean;
  onLogout: () => void;
}

export const UserMenu = ({ isHost, isAuthenticated, isLoggingOut = false, onLogout }: UserMenuProps) => {
  const navigate = useNavigate();
  const [isLoggingOutLocal, setIsLoggingOutLocal] = useState(false);

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoggingOut || isLoggingOutLocal) {
      console.log('Logout already in progress, ignoring click');
      return;
    }
    
    setIsLoggingOutLocal(true);
    
    console.log('Logout button clicked, executing logout function');
    
    onLogout();
    
    setTimeout(() => {
      setIsLoggingOutLocal(false);
    }, 2000);
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Button 
          onClick={() => navigate('/auth')} 
          variant="outline" 
          className="flex items-center gap-1.5"
        >
          <LogIn className="w-4 h-4" />
          <span>Sign in</span>
        </Button>
        <Button 
          onClick={() => navigate('/auth?signup=true')} 
          variant="default" 
          className="flex items-center gap-1.5"
        >
          <UserPlus className="w-4 h-4" />
          <span>Register</span>
        </Button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 transition-colors">
        <Menu className="w-5 h-5" />
        <User className="w-5 h-5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {isAuthenticated && (
          <>
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="w-4 h-4 mr-2" />
              Profile
            </DropdownMenuItem>
            
            <DropdownMenuItem onClick={() => navigate('/profile#security')}>
              <Lock className="w-4 h-4 mr-2" />
              Security Settings
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground px-2">
                Collections
              </DropdownMenuLabel>
              
              <DropdownMenuItem onClick={() => navigate('/collections?tab=my-collections&subtab=created')}>
                <FolderHeart className="w-4 h-4 mr-2" />
                My Collections
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => navigate('/collections?tab=my-collections&subtab=followed')}>
                <Star className="w-4 h-4 mr-2" />
                Followed Collections
              </DropdownMenuItem>
            </DropdownMenuGroup>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground px-2">
                My Trips
              </DropdownMenuLabel>
              
              <DropdownMenuItem onClick={() => navigate('/trips?tab=destinations')}>
                <Bookmark className="w-4 h-4 mr-2" />
                Saved Places
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => navigate('/trips?tab=hosts')}>
                <Users className="w-4 h-4 mr-2" />
                Favorite Hosts
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => navigate('/trips?tab=upcoming')}>
                <Calendar className="w-4 h-4 mr-2" />
                Upcoming Sessions
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => navigate('/trips?tab=past')}>
                <Clock className="w-4 h-4 mr-2" />
                Past Sessions
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => navigate('/trips?tab=bookings')}>
                <Briefcase className="w-4 h-4 mr-2" />
                My Bookings
              </DropdownMenuItem>
            </DropdownMenuGroup>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem asChild>
              <SupportButton variant="ghost" className="w-full cursor-pointer justify-start" />
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={handleLogout} 
              className="text-red-600"
              disabled={isLoggingOut || isLoggingOutLocal}
            >
              <LogOut className="w-4 h-4 mr-2" />
              {isLoggingOut || isLoggingOutLocal ? "Logging out..." : "Log out"}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
